import {
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
	mockNotificationsEnabled,
	getMockNotifications,
	getMockNotification,
	readMockNotification,
	answerMockReview,
} from "./notification-mocks";
export type MonitoringNotification = {
	id: string;
	monitor_id: string;
	read_at: string | null;
	created_at: string;
	review_id: string | null;
	review_status: string | null;
	payload: {
		kind: string;
		action: string;
		level: string;
		metaId: string;
		reasonCode: string;
		metrics: Record<string, string | null>;
		period?: { since: string; until: string };
		before?: string;
		after?: string;
		currency: string;
		conversationId: string | null;
		reviewId: string | null;
		/** Why an advisory did not become an action, when the user can do something about it. */
		note?: string | null;
		// kind === "connection": a team's Meta connection changed; no decision behind it.
		// kind === "creative": what happened to a generated creative (ready, failed, ...).
		event?:
			| "detached" | "replaced" | "tracked_accounts_missing" | "token_invalid" | "opened" | "resolved" | "released"
			| "ready" | "failed" | "unavailable" | "expired" | "ad-rejected";
		reason?: string | null;
		// kind === "creative": the generated image or video and its creative request.
		mediaType?: "image" | "video";
		creativeId?: string;
		// kind === "creative-outage" (super-admins): creative generation fails with this code until fixed.
		errorCode?: string;
		openedAt?: string;
		monitorsSuspended?: number;
		missingAccounts?: string[];
		// kind === "incident": the pipeline could not do something on its own.
		incidentKind?: string;
		audience?: "admin" | "user";
		// kind === "status": Meta is holding the object (review, rejection, issues); no action until ACTIVE.
		effectiveStatus?: string;
		// kind === "unconfirmed": Meta never confirmed a sent change; reason says why, outcome how it ended.
		executionId?: string;
		outcome?: "applied" | "not_applied";
	};
};
export function useNotifications(open: boolean, focusedId?: string | null) {
	const client = useQueryClient();
	const query = useInfiniteQuery({
		queryKey: ["notifications", "list", mockNotificationsEnabled],
		initialPageParam: undefined as string | undefined,
		queryFn: async ({ pageParam }) => mockNotificationsEnabled ? getMockNotifications() : api.getNotifications(pageParam),
		getNextPageParam: (page) => page.nextCursor ?? undefined,
		refetchInterval: open ? 30000 : false,
	});
	const focused = useQuery({
		queryKey: ["notifications", focusedId, mockNotificationsEnabled],
		queryFn: async () => mockNotificationsEnabled ? getMockNotification(focusedId!) : api.getNotification(focusedId!),
		enabled: !!focusedId,
	});
	const invalidate = () =>
		client.invalidateQueries({ queryKey: ["notifications"] });
	const read = useMutation({
		mutationFn: async (id: string) => mockNotificationsEnabled ? readMockNotification(id) : api.readNotification(id),
		onSuccess: invalidate,
	});
	const answer = useMutation({
		mutationFn: async (input: { reviewId: string; action: "approve" | "reject" }) =>
			mockNotificationsEnabled ? answerMockReview(input.reviewId, input.action) : api.answerMonitoringReview(input.reviewId, input.action),
		onSuccess: invalidate,
	});
	const recheck = useMutation({
		mutationFn: async (executionId: string) =>
			mockNotificationsEnabled ? { result: "unreachable" as const, reason: "no_answer" as const } : api.recheckMonitoringExecution(executionId),
		onSuccess: invalidate,
	});
	const items = query.data?.pages.flatMap((page) => page.items) ?? [];
	if (focused.data && !items.some((item) => item.id === focused.data.id))
		items.unshift(focused.data);
	return {
		...query,
		data: query.data
			? { items, unread: query.data.pages[0]?.unread ?? 0 }
			: undefined,
		focused,
		read,
		answer,
		recheck,
	};
}
